/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { HTMLAttributes, useCallback, useEffect, useState } from 'preact/compat';
import { Container, getFormErrors, getFormValues, Slot, SlotProps } from '@adobe-commerce/elsie/lib';
import { InLineAlert, Button, InputFile, TextArea, Input, Field, InLineAlertProps } from '@adobe-commerce/elsie/components';
import { WarningFilled, CheckWithCircle, Add } from '@adobe-commerce/elsie/icons';
import RequestNegotiableQuoteFormComponent from '@/quote-management/components/RequestNegotiableQuoteForm';
import { useText } from '@adobe-commerce/elsie/i18n';
import { events } from '@adobe-commerce/event-bus';
import { requestNegotiableQuote, RequestNegotiableQuoteInput, uploadFile } from '@/quote-management/api';
import { state } from '@/quote-management/lib/state';
import { AttachedFilesList, AttachedFile } from '@/quote-management/components';
import { formatFileSize, validateFileType, validateFileSize } from '@/quote-management/utils/fileUtils';

export type RequestNegotiableQuoteHandlers = {
  onAttachFiles?: (files: File[]) => Promise<void>;
  onRequestNegotiableQuote?: typeof requestNegotiableQuote;
  onSaveNegotiableQuote?: typeof requestNegotiableQuote;
  onSubmitErrors?: (errors: Record<string, string>) => void;
  onError?: (props: {
    error: string,
    isFormDisabled: boolean,
    setIsFormDisabled: (isFormDisabled: boolean) => void,
  }) => void;
};

export interface RequestNegotiableQuoteFormProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onError'>,
  RequestNegotiableQuoteHandlers {
  cartId: string;
  maxFiles?: number;
  maxFileSize?: number;
  acceptedFileTypes?: string[];
  slots?: {
    ErrorBanner?: SlotProps<{
      message: string,
    }>;
    SuccessBanner?: SlotProps<{
      message: string,
    }>;
    Title?: SlotProps<{
      text: string,
    }>;
    CommentField?: SlotProps<{
      formErrors: Record<string, string>;
      isFormDisabled: boolean;
      setFormErrors: (errors: Record<string, string>) => void;
    }>;
    QuoteNameField?: SlotProps<{
      formErrors: Record<string, string>;
      isFormDisabled: boolean;
      setFormErrors: (errors: Record<string, string>) => void;
    }>;
    AttachFileField?: SlotProps<{
      onChange: (files: File[]) => void, formErrors: Record<string, string>,
      isFormDisabled: boolean,
      attachedFiles: AttachedFile[]
    }>;
    AttachedFilesList?: SlotProps<{
      files: AttachedFile[];
      onRemove: (key: string) => void;
      disabled?: boolean;
    }>;
    RequestButton?: SlotProps<{
      requestNegotiableQuote: typeof requestNegotiableQuote;
      formErrors: Record<string, string>;
      isFormDisabled: boolean;
      setIsFormDisabled: (isFormDisabled: boolean) => void;
    }>;
    SaveDraftButton?: SlotProps<{
      requestNegotiableQuote: typeof requestNegotiableQuote;
      formErrors: Record<string, string>;
      isFormDisabled: boolean;
      setIsFormDisabled: (isFormDisabled: boolean) => void;
    }>;
  };
}

export const RequestNegotiableQuoteForm: Container<RequestNegotiableQuoteFormProps> = ({
  cartId,
  slots,
  onRequestNegotiableQuote,
  onSaveNegotiableQuote,
  onAttachFiles,
  onSubmitErrors,
  onError,
  className,
  maxFiles,
  maxFileSize,
  acceptedFileTypes,
}) => {
  const [comment, setComment] = useState<string | undefined>(undefined);
  const [quoteName, setQuoteName] = useState<string | undefined>(undefined);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [error, setError] = useState<string | undefined>(undefined);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState<string | undefined>(undefined);
  const [isFormDisabled, setIsFormDisabled] = useState<boolean>(false);

  const dictionary = useText({
    title: 'NegotiableQuote.Request.title',
    comment: 'NegotiableQuote.Request.comment',
    commentError: 'NegotiableQuote.Request.commentError',
    quoteName: 'NegotiableQuote.Request.quoteName',
    quoteNameError: 'NegotiableQuote.Request.quoteNameError',
    attachmentsError: 'NegotiableQuote.Request.attachmentsError',
    maxFilesExceeded: 'NegotiableQuote.Request.maxFilesExceeded',
    maxFileSizeExceeded: 'NegotiableQuote.Request.maxFileSizeExceeded',
    invalidFileType: 'NegotiableQuote.Request.invalidFileType',
    requestCta: 'NegotiableQuote.Request.requestCta',
    saveDraftCta: 'NegotiableQuote.Request.saveDraftCta',
    errorHeader: 'NegotiableQuote.Request.error.header',
    unauthenticated: 'NegotiableQuote.Request.error.unauthenticated',
    unauthorized: 'NegotiableQuote.Request.error.unauthorized',
    missingCart: 'NegotiableQuote.Request.error.missingCart',
    successHeader: 'NegotiableQuote.Request.success.header',
    submitSuccess: 'NegotiableQuote.Request.success.submitted',
    draftSuccess: 'NegotiableQuote.Request.success.draftSaved',
  });

  useEffect(() => {
    const permissionsEvent = events.on(
      'quote-management/permissions',
      (permissions: typeof state.permissions) => {
        setError(undefined);
        if (!permissions.requestQuote) {
          setError(dictionary.unauthorized);
          setIsFormDisabled(true);
        }
        else {
          setIsFormDisabled(false);
        }
      },
      { eager: true }
    );
    return () => permissionsEvent?.off();
  }, [dictionary.unauthorized]);

  useEffect(() => {
    const authenticatedEvent = events.on(
      'authenticated',
      (authenticated: boolean) => {
        setError(undefined);
        if (!authenticated) {
          setError(dictionary.unauthenticated);
          setIsFormDisabled(true);
        }
        else {
          setIsFormDisabled(false);
        }
      },
      { eager: true }
    );
    return () => authenticatedEvent?.off();
  }, [dictionary.unauthenticated]);

  useEffect(() => {
    if (!cartId) {
      setError(dictionary.missingCart);
      setIsFormDisabled(true);
    }
  }, [cartId, dictionary.missingCart]);

  useEffect(() => {
    if (error) {
      onError?.({ error, isFormDisabled, setIsFormDisabled });
    }
  }, [error, onError, isFormDisabled]);

  const handleAttachFiles = useCallback(async (files: File[]) => {
    /* istanbul ignore next: defensive guard; */
    if (!files?.length) return;

    // Check maxFiles limit
    if (maxFiles && attachedFiles.length + files.length > maxFiles) {
      setFormErrors(prev => ({
        ...prev,
        attachments: dictionary.maxFilesExceeded.replace('{maxFiles}', String(maxFiles))
      }));
      return;
    }

    // Validate file types and sizes before uploading
    for (const file of files) {
      if (maxFileSize && !validateFileSize(file.size, maxFileSize)) {
        setFormErrors(prev => ({
          ...prev,
          attachments: dictionary.maxFileSizeExceeded.replace('{maxSize}', formatFileSize(maxFileSize))
        }));
        return;
      }

      if (acceptedFileTypes && !validateFileType(file.type, acceptedFileTypes)) {
        setFormErrors(prev => ({ ...prev, attachments: dictionary.invalidFileType }));
        return;
      }
    }

    // Set form errors without attachments key
    setFormErrors(prev => { 
      // Disable eslint rule for unused attachments key
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { attachments, ...rest } = prev;
      return rest;
    });


    if (onAttachFiles) {
      onAttachFiles(files)?.catch(() => {
        setFormErrors(prev => ({ ...prev, attachments: dictionary.attachmentsError }));
      });
      return;
    }

    // Add files with 'uploading' status
    const newFiles: AttachedFile[] = files.map(file => ({
      key: `temp-${Date.now()}-${Math.random()}-${file.name}`,
      name: file.name,
      size: file.size,
      status: 'uploading',
    }));

    setAttachedFiles(prev => [...prev, ...newFiles]);

    // Upload each file and update its status
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const tempKey = newFiles[i].key;

      try {
        const result = await uploadFile(file);
        setAttachedFiles(prev => prev.map(f =>
          f.key === tempKey
            ? { ...f, key: result.key, status: 'success' }
            : f
        ));
      } catch (error) {
        setFormErrors(prev => ({ ...prev, attachments: (error as Error).message }));
        setAttachedFiles(prev => prev.map(f =>
          f.key === tempKey
            ? { ...f, status: 'error', error: (error as Error).message }
            : f
        ));
      }
    }
  }, [onAttachFiles, dictionary, maxFiles, maxFileSize, acceptedFileTypes, attachedFiles]);

  const handleRemoveFile = useCallback((key: string) => {
    setAttachedFiles(prev => prev.filter(f => f.key !== key));
  }, []);

  /*
  * This function is used to get the banner node.
  * It can be used to render a success or error banner depending on the success or error state.
  */
  const getBannerNode = () => {
    let inlineAlertProps: InLineAlertProps | undefined;
    let slotProps: any | undefined;

    if (success) {
      slotProps = {
        name: 'SuccessBanner',
        slot: slots?.SuccessBanner,
        context: {
          message: success,
        },
        'data-testid': 'form-success-banner',
      };
      inlineAlertProps = {
        type: 'success',
        variant: 'primary',
        icon: <CheckWithCircle />,
        heading: dictionary.successHeader,
        description: success,
        className: 'request-negotiable-quote-form__success-banner',
      };
    }
    else if (error) {
      slotProps = {
        name: 'ErrorBanner',
        slot: slots?.ErrorBanner,
        context: {
          message: error,
        },
        'data-testid': 'form-error-banner',
      };
      inlineAlertProps = {
        type: 'error',
        variant: 'primary',
        icon: <WarningFilled />,
        heading: dictionary.errorHeader,
        description: error,
        className: 'request-negotiable-quote-form__error-banner',
      };
    }

    if (slotProps && inlineAlertProps) {
      return <Slot {...slotProps}>
        <InLineAlert {...inlineAlertProps} />
      </Slot>;
    }

    return undefined;
  }

  /*
  * This function is used to validate the parent form of the button that was clicked.
  */
  const validateParentForm = (e: Event) => {
    setFormErrors({});
    const closestForm = (e.target as HTMLElement).closest('form') as HTMLFormElement;
    const formErrors = getFormErrors(closestForm);
    if (Object.keys(formErrors).length > 0) {
      setFormErrors(formErrors);
      onSubmitErrors?.(formErrors);
    }
  };

  const formSubmitHandler = (e: Event) => {
    e.preventDefault();
    setIsFormDisabled(true);

    const form = (e.target as HTMLFormElement);

    const currentFormErrors = getFormErrors(form);

    const mergedFormErrors = { ...currentFormErrors, ...formErrors };

    if (Object.keys(mergedFormErrors).length > 0) {
      onSubmitErrors?.(mergedFormErrors)
      return;
    }

    // Since we have multiple submit buttons, we need to determine which one was clicked.
    const buttonTarget = (e as SubmitEvent).submitter as HTMLButtonElement;

    const formValues = getFormValues(form);

    setComment(formValues.comment);
    setQuoteName(formValues.quoteName);

    const isDraft = buttonTarget?.dataset?.draft === 'true' || false;

    // Only include successfully uploaded files
    const successfulAttachments = attachedFiles
      .filter(f => f.status === 'success')
      .map(f => ({ key: f.key }));

    const contextData: RequestNegotiableQuoteInput = {
      cartId: cartId!,
      quoteName: formValues.quoteName,
      comment: formValues.comment,
      attachments: successfulAttachments,
      isDraft,
    }

    let submitHandler;
    let successMessage;

    if (isDraft) {
      submitHandler = onSaveNegotiableQuote ?? requestNegotiableQuote;
      successMessage = dictionary.draftSuccess;
    } else {
      submitHandler = onRequestNegotiableQuote ?? requestNegotiableQuote;
      successMessage = dictionary.submitSuccess;
    }

    submitHandler(contextData).then(() => {
      setSuccess(successMessage);
    })
      .catch(error => {
        setError(error.message);
      })
  };

  const titleNode = (
    <Slot name="Title" slot={slots?.Title} context={{ text: dictionary.title }}>
      <span data-testid="form-title">{dictionary.title}</span>
    </Slot>
  );

  const commentFieldNode = (
    <Slot
      name="CommentField"
      slot={slots?.CommentField}
      context={{
        value: comment,
        required: true,
        errorMessage: formErrors.comment,
        setFormErrors,
        isFormDisabled
      } as any}
    >
      <TextArea
        name="comment"
        value={comment}
        label={dictionary.comment}
        required
        autoComplete="off"
        data-testid="form-comment-field"
        errorMessage={formErrors.comment}
        disabled={isFormDisabled}
      />
    </Slot>
  );

  const quoteNameFieldNode = (
    <Slot
      name="QuoteNameField"
      slot={slots?.QuoteNameField}
      context={{
        value: quoteName,
        required: true,
        errorMessage: formErrors.quoteName,
        setFormErrors,
        isFormDisabled
      } as any}
    >
      <Field
        error={formErrors.quoteName}
        disabled={isFormDisabled}
      >
        <Input
          value={quoteName}
          name="quoteName"
          floatingLabel={dictionary.quoteName}
          required
          autoComplete="off"
          data-testid="form-quote-name-field"
        />
      </Field>
    </Slot>
  );

  const attachFileNode = (
    <Slot
      name="AttachFileField"
      slot={slots?.AttachFileField}
      context={{ onChange: handleAttachFiles, formErrors, isFormDisabled, attachedFiles }}
    >
      <InputFile
        multiple
        onChange={(e: Event) => {
          const target = e.target as HTMLInputElement;
          const fileList = target?.files;
          const files = fileList ? Array.from(fileList) : [];

          if (files.length > 0) {
            void handleAttachFiles(files);
          }
        }}
        icon={<Add />}
        disabled={isFormDisabled}
        data-testid="form-attach-file-field"
        accept={acceptedFileTypes?.join(',')}
      />
    </Slot>
  );

  const attachedFilesListNode = attachedFiles.length > 0 ? (
    <Slot
      name="AttachedFilesList"
      slot={slots?.AttachedFilesList}
      context={{ files: attachedFiles, onRemove: handleRemoveFile, disabled: isFormDisabled }}
    >
      <AttachedFilesList
        files={attachedFiles}
        onRemove={handleRemoveFile}
        disabled={isFormDisabled}
      />
    </Slot>
  ) : undefined;

  const requestButtonNode = (
    <Slot
      name="RequestButton"
      slot={slots?.RequestButton}
      context={
        {
          requestNegotiableQuote,
          formErrors,
          isFormDisabled,
          setIsFormDisabled
        }
      }
    >
      <Button
        type="submit"
        data-testid="form-request-button"
        onClick={validateParentForm}
        disabled={isFormDisabled}
      >
        {dictionary.requestCta}
      </Button>
    </Slot>
  );

  const saveButtonNode = (
    <Slot
      name="SaveDraftButton"
      slot={slots?.SaveDraftButton}
      context={
        {
          requestNegotiableQuote,
          formErrors,
          isFormDisabled,
          setIsFormDisabled
        }
      }
    >
      <Button
        type="submit"
        data-draft="true"
        variant="secondary"
        data-testid="form-save-draft-button"
        onClick={validateParentForm}
        disabled={isFormDisabled}
      >
        {dictionary.saveDraftCta}
      </Button>
    </Slot>
  );

  return (
    <RequestNegotiableQuoteFormComponent
      title={titleNode}
      banner={getBannerNode()}
      commentField={commentFieldNode}
      quoteNameField={quoteNameFieldNode}
      attachFile={attachFileNode}
      attachedFilesList={attachedFilesListNode}
      requestButton={requestButtonNode}
      saveButton={saveButtonNode}
      onSubmit={formSubmitHandler}
      className={className}
      disabled={isFormDisabled}
      data-testid="form-container"
    />
  );
};
